import { ContactMail, IosShare, RestartAltOutlined, KeyboardOptionKey, Mic, Stop, StickyNote2Outlined, EmojiObjectsOutlined as EmojiObjectsIcon, FolderOutlined as FolderIcon, GraphicEq, Schedule as ScheduleIcon } from "@mui/icons-material";
import Add from "@mui/icons-material/Add";
import AddPhotoAlternateOutlined from "@mui/icons-material/AddPhotoAlternateOutlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import SendIcon from "@mui/icons-material/Send";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import UploadFileOutlined from "@mui/icons-material/UploadFileOutlined";
import ContentCopyOutlined from "@mui/icons-material/ContentCopyOutlined";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import capture from "../../capture";
import ChatConversation from "../../components/ChatConversation";
import LoadingScreen from "../../components/LoadingScreen";
import LucideIcon from "../../components/LucideIcon";
import PopOutTextDialog from "../../components/PopOutTextDialog";
import SelectEmployeeDialog from "../../components/SelectEmployee";
import { defaultModel, STRATEGIES, DOCUMENT_ACCEPT_TYPES, isAllowedDocumentType, ALLOWED_DOCUMENT_EXTENSIONS, DOCUMENT_TYPE_LABELS } from "../../constants";
import { useWhitelabel } from "../../context/WhitelabelContext";
import { Company, Document, Employee, Message, WorkspaceProject, ProjectFile } from "../../types";
import { fileToBase64, hostname, parseError, addImageDimensions, getSessionMetadata } from "../../utils";
import { hasAdminAccessByRole } from "../../utils/roleUtils";
import { useEmployeeTerminology } from "../../utils/terminology";
import { useDictation } from "../../hooks/useDictation";
import ChatApi from "../chat/chatApi";
import ChatLog from "../chat/ChatLog";
import ChatContext from "./ChatContext";
import ChatSettingsDialog from "./ChatSettingsDialog";
import QuoteRequestDialog from "./dialogs/QuoteRequestDialog";
import MemoriesDialog from "./dialogs/MemoriesDialog";
import ScheduledTasksDialog from "./dialogs/ScheduledTasksDialog";
import ShareChatDialog from "./ShareChatDialog";
import InstallIntegrationDialog from "../integrations/InstallIntegrationDialog";
import ProjectFilesDialog from "./dialogs/ProjectFilesDialog";
import FileViewerDialog from "./dialogs/FileViewerDialog";

export default ({
  currentUser,
  id,
  companyId,
  token,
  getToken,
  eventListener,
  mode,
  instanceConfig,
  currentPlan,
  company,
  employees,
  usage,
  userRole,
}: {
  currentUser: any | User;
  id: string | any;
  companyId: string;
  token: any | string;
  getToken: Function;
  eventListener: Function;
  mode: string;
  instanceConfig: any;
  currentPlan: string;
  company: Company;
  employees: Array<Employee>;
  usage: any;
  userRole?: string | null;
}) => {
  const { whitelabelSettings } = useWhitelabel();
  const { employee: employeeText, employeeLower } = useEmployeeTerminology();
  const [prompt, setPrompt] = useState("");
  const [chat, setChat] = useState([]);
  const [employee, setEmployee] = useState<Employee>({
    uid: "",
    id: "",
    name: "",
    title: "",
    companyId: "",
    prompt: "",
    tags: [],
    picture: "",
  });
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const [init, setInit] = useState(false);
  const [recording, setRecording] = useState(false);
  // Track messages that were in loading state when chat was first loaded
  // These are messages from interrupted sessions that need SSE streaming to complete
  const initialLoadingMessageIds = useRef<Set<string>>(new Set());
  const messageStreamCleanups = useRef<Map<string, () => void>>(new Map());
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [documents, setDocuments] = useState<Array<Document>>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | false>(false);
  const [selectEmployeesOpen, setSelectEmployeesOpen] = useState(false);
  const chatLogRef = useRef();
  const defaultContextSettings = {
    model: defaultModel,
    memoryScope: "chat",
    searchEngine: false,
    shortTermMemories: true,
    longTermMemories: true,
    documents: true,
    company: true,
    employee: true,
    responseInstructions: true,
    strategy: "function-chain",
    websites: false,
    websitesJs: false,
    documentScope: "root",
    documentPath: "/",
    documentStrategy: "similarity-ranking",
    mcpServers: [],
  };
  const [contextSettings, setContextSettings] = useState(defaultContextSettings);
  const [dialogSelectedMessage, setDialogSelectedMessage] =
    useState<Message | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [folderPath, setFolderPath] = useState("/");
  const [images, setImages] = useState<Array<{ name: string; content: string }>>([]);
  const [requestDocuments, setRequestDocuments] = useState<Array<any>>([]);
  const [documentProcessing, setDocumentProcessing] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [strategyOptions, setStrategyOptions] = useState(STRATEGIES);
  const [shareOpen, setShareOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [availableModels, setAvailableModels] = useState<Set<string>>(new Set());
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchorEl);
  const [popOutDialogOpen, setPopOutDialogOpen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);
  const [promptsDialogOpen, setPromptsDialogOpen] = useState(false);
  const [composioInstallOpen, setComposioInstallOpen] = useState(false);
  const [composioInstallSlug, setComposioInstallSlug] = useState<string | null>(null);
  const [memoriesDialogOpen, setMemoriesDialogOpen] = useState(false);
  const [scheduledTasksDialogOpen, setScheduledTasksDialogOpen] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState<Array<{ id: string; name: string; content: string; description: string }>>([]);
  const [promptsLoading, setPromptsLoading] = useState(false);
  // Track which chat is currently sending (null if none)
  // This allows switching to other chats while one is in progress
  const [sendingChatId, setSendingChatId] = useState<string | null>(null);
  // AbortController for the in-flight stream so the user can pause it
  const abortControllerRef = useRef<AbortController | null>(null);
  // Voice chat state
  const [isVoiceChatActive, setIsVoiceChatActive] = useState(false);
  const audioQueueRef = useRef<{ url: string; text: string }[]>([]);
  const isPlayingAudioRef = useRef(false);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  // Project state
  const [currentProject, setCurrentProject] = useState<WorkspaceProject | null>(null);
  const [projectFilesDialogOpen, setProjectFilesDialogOpen] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<ProjectFile | null>(null);
  const dragCounterRef = useRef(0);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTopRef = useRef(0);
  const navigate = useNavigate();
  const chatApi = new ChatApi();
  const theme = useTheme();
  const displayValue = id ? "block" : "none";
  chatApi.init(eventListener, getToken);

  // Dictation hook for voice-to-text
  const {
    isRecording: isDictating,
    isLoading: isDictationLoading,
    startDictation,
    stopDictation,
    error: dictationError,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim()) {
        setPrompt((prev) => {
          const trimmedPrev = prev.trim();
          const trimmedText = text.trim();
          // Append the transcript with a space if there's existing text
          return trimmedPrev ? `${trimmedPrev} ${trimmedText}` : trimmedText;
        });
      }
    },
    onError: (error) => {
      eventListener({
        type: "SET_ERROR",
        error,
      });
    },
  });

  // Voice chat state refs for stable references
  const isVoiceChatActiveRef = useRef(false);
  const isVoiceProcessingRef = useRef(false);

  // Voice chat dictation hook - separate from regular dictation
  const {
    isRecording: isVoiceRecording,
    startDictation: startVoiceDictation,
    stopDictation: stopVoiceDictation,
    transcript: voiceTranscript,
  } = useDictation({
    onTranscript: (text, isFinal) => {
      if (isFinal && text.trim() && isVoiceChatActiveRef.current && !isVoiceProcessingRef.current) {
        // Stop dictation immediately to prevent duplicate inputs
        stopVoiceDictation();
        sendVoiceMessage(text.trim());
      }
    },
    onError: (error) => {
      eventListener({
        type: "SET_ERROR",
        error,
      });
      setIsVoiceChatActive(false);
      isVoiceChatActiveRef.current = false;
    },
  });

  // Store stopVoiceDictation in ref for stable access in callbacks
  const stopVoiceDictationRef = useRef(stopVoiceDictation);
  stopVoiceDictationRef.current = stopVoiceDictation;
  const startVoiceDictationRef = useRef(startVoiceDictation);
  startVoiceDictationRef.current = startVoiceDictation;

  // Play audio queue for voice chat
  const playNextAudio = useCallback(() => {
    if (audioQueueRef.current.length === 0) {
      isPlayingAudioRef.current = false;
      // Start listening again after audio finishes (if still in voice chat mode and not processing)
      if (isVoiceChatActiveRef.current && !isVoiceProcessingRef.current) {
        startVoiceDictationRef.current();
      }
      return;
    }

    const next = audioQueueRef.current.shift()!;
    audioPlayerRef.current = new Audio(next.url);
    audioPlayerRef.current.onended = () => {
      URL.revokeObjectURL(next.url);
      playNextAudio();
    };
    audioPlayerRef.current.onerror = () => {
      URL.revokeObjectURL(next.url);
      playNextAudio();
    };
    audioPlayerRef.current.play().catch((e) => {
      console.error("Audio playback failed:", e);
      URL.revokeObjectURL(next.url);
      playNextAudio();
    });
  }, []);

  // Handle audio chunks from voice chat
  useEffect(() => {
    const handleAudioChunk = (event: CustomEvent) => {
      if (!isVoiceChatActiveRef.current) return;

      const { format, data, text } = event.detail;
      // Decode base64 audio
      const audioData = atob(data);
      const audioBytes = new Uint8Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        audioBytes[i] = audioData.charCodeAt(i);
      }
      const audioBlob = new Blob([audioBytes], { type: `audio/${format}` });
      const audioUrl = URL.createObjectURL(audioBlob);

      audioQueueRef.current.push({ url: audioUrl, text });

      // Start playing if not already
      if (!isPlayingAudioRef.current) {
        isPlayingAudioRef.current = true;
        playNextAudio();
      }
    };

    window.addEventListener('voiceAudioChunk', handleAudioChunk as EventListener);
    return () => {
      window.removeEventListener('voiceAudioChunk', handleAudioChunk as EventListener);
    };
  }, [playNextAudio]);

  // Send voice message with audio response
  const sendVoiceMessage = async (text: string) => {
    if (!text.trim() || !employee || !company) return;

    isVoiceProcessingRef.current = true;
    setSendingChatId(id);
    try {
      await chatApi.sendPyPrompt(
        text,
        chat,
        documents,
        setChat,
        employee,
        contextSettings,
        setPrompt,
        refreshChat,
        company,
        currentUser,
        id,
        true, // useStructuredResponse
        usage?.photo || "",
        [],
        usage?.displayName || currentUser.displayName || "You",
        true // includeAudio
      );
    } finally {
      setSendingChatId(null);
      isVoiceProcessingRef.current = false;
      // If audio is not playing, restart dictation
      if (isVoiceChatActiveRef.current && !isPlayingAudioRef.current) {
        startVoiceDictationRef.current();
      }
    }
  };

  // Toggle voice chat mode
  const toggleVoiceChat = () => {
    if (isVoiceChatActive) {
      // Stop voice chat
      setIsVoiceChatActive(false);
      isVoiceChatActiveRef.current = false;
      stopVoiceDictationRef.current();
      // Stop any playing audio
      if (audioPlayerRef.current) {
        audioPlayerRef.current.pause();
        audioPlayerRef.current = null;
      }
      // Clear audio queue
      audioQueueRef.current.forEach(item => URL.revokeObjectURL(item.url));
      audioQueueRef.current = [];
      isPlayingAudioRef.current = false;
    } else {
      // Start voice chat
      setIsVoiceChatActive(true);
      isVoiceChatActiveRef.current = true;
      startVoiceDictationRef.current();
    }
  };

  // Cleanup voice chat on unmount or chat change
  useEffect(() => {
    return () => {
      if (isVoiceChatActiveRef.current) {
        stopVoiceDictationRef.current();
        isVoiceChatActiveRef.current = false;
      }
    };
  }, [id]);

  // Listen for openComposioInstaller events from tool outputs
  useEffect(() => {
    const handleOpenComposioInstaller = (event: CustomEvent) => {
      const { slug } = event.detail;
      setComposioInstallSlug(slug);
      setComposioInstallOpen(true);
    };

    window.addEventListener("openComposioInstaller", handleOpenComposioInstaller as EventListener);
    return () => {
      window.removeEventListener("openComposioInstaller", handleOpenComposioInstaller as EventListener);
    };
  }, []);

  // Listen for openMemoriesDialog events from memory tool cards
  useEffect(() => {
    const handleOpenMemoriesDialog = () => {
      setMemoriesDialogOpen(true);
    };

    window.addEventListener("openMemoriesDialog", handleOpenMemoriesDialog as EventListener);
    return () => {
      window.removeEventListener("openMemoriesDialog", handleOpenMemoriesDialog as EventListener);
    };
  }, []);

  const getMessageBorderRadius = (role: String) => {
    return role !== "user" ? "20px 20px 20px 1px" : "20px 20px 1px 20px";
  };

  const createChat = async (employee: Employee) => {
    capture("Created new chat", {});
    const currentToken = await getToken();
    const chatId = uuidv4();
    await axios
      .post(
        `${hostname}/chats`,
        {
          id: chatId,
          companyId: companyId,
          ts: new Date().getTime(),
          employeeId: employee.id,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    if (chatLogRef && chatLogRef.current) {
      // @ts-ignore
      chatLogRef.current.refreshChats();
    }
    navigate("/dashboard/companies/" + companyId + "/chats/" + chatId);
  };

  const restartChat = async (employee: Employee) => {
    capture("Restarted chat", {});
    const currentToken = await getToken();
    const chatId = uuidv4();
    await axios
      .post(
        `${hostname}/chats`,
        {
          id: chatId,
          companyId: companyId,
          ts: new Date().getTime(),
          employeeId: employee.id,
          settings: contextSettings,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    if (chatLogRef && chatLogRef.current) {
      // @ts-ignore
      chatLogRef.current.refreshChats();
    }
    navigate("/dashboard/companies/" + companyId + "/chats/" + chatId);
  };

  const getDocuments = async (
    path: string,
    selectedDoc: Document | undefined = undefined
  ) => {
    const currentToken = await getToken();
    await axios
      .get(`${hostname}/documents`, {
        headers: { Authorization: `Bearer ${currentToken}` },
        params: { path: path, companyId: companyId },
      })
      .then((res) => {
        var documentList = res.data;
        if (selectedDoc) {
          setDocuments([selectedDoc, ...documentList]);
        } else {
          setDocuments(documentList);
        }
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
  };

  const fetchSavedPrompts = async () => {
    setPromptsLoading(true);
    try {
      const currentToken = await getToken();
      const response = await axios.get(`${hostname}/content/prompts`, {
        params: { companyId, page: 1, pageSize: 100 },
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });
      if (response.data) {
        setSavedPrompts(response.data.items || []);
      }
    } catch (err) {
      console.error("Error fetching prompts:", err);
    } finally {
      setPromptsLoading(false);
    }
  };

  const handleSelectPrompt = (promptContent: string) => {
    setPrompt((prev) => {
      const trimmedPrev = prev.trim();
      // Append prompt content with a newline if there's existing text
      return trimmedPrev ? `${trimmedPrev}\n\n${promptContent}` : promptContent;
    });
    setPromptsDialogOpen(false);
    setMenuAnchorEl(null);
  };

  const getData = async () => {
    if (!id) {
      setLoading(false);
      setChatLoading(false);
      return;
    }
    const currentToken = await getToken();
    const empFuture = axios
      .get(`${hostname}/chats/${companyId}/${id}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        var docFuture;
        const currentChat = res.data.chat;
        if (!currentChat) {
          // Chat not found or not yet created, use defaults
          docFuture = getDocuments(folderPath);
          return Promise.all([docFuture]);
        }
        if (currentChat.settings) {
          // Use defaults as base, then apply chat settings - never inherit from previous chat
          var newSettings = { ...defaultContextSettings, ...currentChat.settings };
          setContextSettings(newSettings);
          docFuture = getDocuments(newSettings.documentPath);
        } else {
          setContextSettings(defaultContextSettings);
          docFuture = getDocuments(folderPath);
        }
        const currentEmployee = employees.find(
          (x: any) => x.id === currentChat.employeeId
        );
        if (currentEmployee) {
          setEmployee(currentEmployee);
        }
        return Promise.all([docFuture]);
      })
      .catch((err) => {
        // Handle 404 or other errors gracefully
        if (err.response?.status === 404) {
          // Chat doesn't exist yet, use defaults
          return Promise.all([getDocuments(folderPath)]);
        }
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
        return Promise.all([getDocuments(folderPath)]);
      });

    const chatFuture = chatApi
      .getMessages(companyId, id)
      .then((messages: any) => {
        setChat(messages);
        // Track messages that are in loading state on initial load
        // These are from interrupted sessions where the backend is still processing
        const loadingIds = messages
          .filter((msg: Message) => msg.loading === true)
          .map((msg: Message) => msg.id);
        initialLoadingMessageIds.current = new Set(loadingIds);
        // Signal that initial load is complete and we can start streaming
        setInitialLoadComplete(true);
      });
    await Promise.all([empFuture, chatFuture]);
    setLoading(false);
    setChatLoading(false);
  };

  // Fetch models on component mount to determine vision support
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const token = await getToken();
        const response = await axios.get(`${hostname}/api/v0/models`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Build a Set of model names that support vision
        const visionModelNames = new Set<string>(
          response.data.models
            .filter((model: any) => model.visionModel === true)
            .map((model: any) => model.name)
        );
        setAvailableModels(visionModelNames);
      } catch (err) {
        console.error("Error fetching models:", err);
      }
    };

    if (currentUser.uid) {
      fetchModels();
    }
  }, [currentUser.uid]);

  // Clear images when switching to a model that doesn't support vision
  useEffect(() => {
    // Only check if we have models loaded and images attached
    if (availableModels.size > 0 && images.length > 0) {
      if (!availableModels.has(contextSettings.model)) {
        // Current model doesn't support images, clear them and show error
        setImages([]);
        eventListener({
          type: "SET_ERROR",
          error: "The selected model does not support images. Your attached images have been removed.",
        });
      }
    }
  }, [contextSettings.model, availableModels]);

  // Fetch project when contextSettings has a projectId
  useEffect(() => {
    const fetchProject = async () => {
      const projectId = (contextSettings as any).projectId;
      if (projectId && projectId !== currentProject?.id) {
        try {
          const token = await getToken();
          const response = await axios.get(
            `${hostname}/projects/${projectId}`,
            {
              params: { companyId },
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setCurrentProject(response.data);
        } catch (error) {
          console.error("Error fetching project:", error);
          setCurrentProject(null);
        }
      } else if (!projectId && currentProject) {
        setCurrentProject(null);
      }
    };
    fetchProject();
  }, [(contextSettings as any).projectId, companyId]);

  useEffect(() => {
    if (currentUser.uid && company && employees) {
      setChatLoading(true);
      setInit(true);
      // Clear tracking when switching chats
      setInitialLoadComplete(false);
      initialLoadingMessageIds.current.clear();
      // Clear scroll timeout and reset last scroll position
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      lastScrollTopRef.current = 0;
      getData();
    }
  }, [id, currentUser, companyId, employees, company]);

  // Reset auto-scroll when loading new chat with delay to ensure messages are rendered
  useEffect(() => {
    if (!chatLoading && !loading && id && chat.length > 0) {
      setAutoScroll(true); // Enable auto-scroll on initial load
      // Add delay to ensure messages are fully rendered before scrolling
      setTimeout(() => {
        if (messagesEndRef.current) {
          messagesEndRef.current.scrollIntoView({ behavior: "auto" });
        }
      }, 100);
    }
  }, [chatLoading, loading, id, chat.length]); // Run when loading completes or chat ID changes

  // Detect user scroll and toggle auto-scroll
  const handleScroll = () => {
    if (!chatContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 100; // 100px threshold

    // Detect if user scrolled up (scrollTop decreased)
    const scrolledUp = scrollTop < lastScrollTopRef.current - 5; // 5px threshold to avoid minor fluctuations

    // Update last scroll position
    lastScrollTopRef.current = scrollTop;

    // If user scrolled up, immediately disable auto-scroll
    if (scrolledUp && autoScroll) {
      setAutoScroll(false);
      return;
    }

    // Clear any existing timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    // Debounce scroll detection for re-enabling auto-scroll when at bottom
    scrollTimeoutRef.current = setTimeout(() => {
      if (isAtBottom && !autoScroll) {
        setAutoScroll(true);
      }
    }, 150); // Wait 150ms after scroll stops
  };

  // Stream updates for messages that were interrupted from previous sessions
  // This handles the edge case where:
  // 1. User sends a message and streaming starts
  // 2. User closes browser/tab mid-stream
  // 3. Backend continues processing (message stays loading: true in DB)
  // 4. User reopens chat before backend completes
  // 5. We open an SSE stream to get real-time updates until completion
  useEffect(() => {
    if (!initialLoadComplete || !id || !companyId || initialLoadingMessageIds.current.size === 0) {
      return;
    }

    // Start streaming for each message that was loading on initial load
    initialLoadingMessageIds.current.forEach((messageId) => {
      // Skip if already streaming
      if (messageStreamCleanups.current.has(messageId)) {
        return;
      }

      console.log(`Starting SSE stream for message ${messageId}`);

      // Open SSE stream for this message
      chatApi
        .streamMessageUpdates(
          companyId,
          id,
          messageId,
          // onUpdate: Update message in chat
          (updatedMessage: any) => {
            setChat((prevChat: any) =>
              prevChat.map((m: Message) =>
                m.id === messageId ? updatedMessage : m
              )
            );
          },
          // onComplete: Remove from tracking sets
          () => {
            console.log(`SSE stream completed for message ${messageId}`);
            messageStreamCleanups.current.delete(messageId);
            initialLoadingMessageIds.current.delete(messageId);
          },
          // onError: Stop streaming on error
          (error: string) => {
            console.error(`Error streaming message ${messageId}:`, error);
            messageStreamCleanups.current.delete(messageId);
            initialLoadingMessageIds.current.delete(messageId);
          }
        )
        .then((cleanup) => {
          // Store cleanup function to close stream later if needed
          messageStreamCleanups.current.set(messageId, cleanup);
        });
    });

    // Cleanup function - only runs on unmount or when chat changes
    return () => {
      console.log("Cleaning up SSE streams");
      messageStreamCleanups.current.forEach((cleanup) => cleanup());
      messageStreamCleanups.current.clear();
    };
  }, [initialLoadComplete, id, companyId]); // Only re-run when initial load completes or chat ID changes

  const refreshChat = async (
    userMessage: Message,
    assistantMessage: Message
  ) => {
    await chatApi
      .getMessageContext(companyId, id, assistantMessage.id)
      .then((msgContext) => {
        setChat((chat: any) =>
          chat.map((x: Message) => {
            if (x.id === assistantMessage.id) {
              return {
                ...x,
                context: msgContext,
              };
            }
            return x;
          })
        );
      });
    if (chatLogRef && chatLogRef.current) {
      // @ts-ignore
      chatLogRef.current.refreshChats();
    }
  };

  const handleFirstChatSelection = (firstChatId: string) => {
    // Only auto-select on desktop (md and above)
    const isMobile = window.innerWidth < theme.breakpoints.values.md;
    if (!isMobile && !id && firstChatId) {
      navigate(`/dashboard/companies/${companyId}/chats/${firstChatId}`);
    }
  };

  const sendMessage = async () => {
    if (prompt === "") return;

    // If a previous request is still streaming, abort it so its assistant
    // placeholder is dropped from the chat (the AbortError path in chatApi
    // removes it). The backend continues to completion and saves whatever
    // partial response it produced — but the UI starts fresh for the new turn.
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Track this chat as sending.
    setSendingChatId(id);

    // Create an abort controller so the user can pause this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    // Enable auto-scroll when user sends a message
    setAutoScroll(true);

    // Build attachments array from images and requestDocuments
    const attachments: Array<{ name: string; type: string; content: string }> = [];

    // Add all images
    for (const img of images) {
      attachments.push({
        name: img.name || "image",
        type: img.content.split(";")[0].split(":")[1] || "image/png", // Extract type from data URI
        content: img.content,
      });
    }

    // Add all documents
    for (const doc of requestDocuments) {
      attachments.push({
        name: doc.name || "document",
        type: "text/plain", // Documents are already converted to text
        content: doc.content || "",
      });
    }

    // Get user display name - prefer usage.displayName, then currentUser.displayName, then email username
    const getUserDisplayName = () => {
      if (usage?.displayName) return usage.displayName;
      if (currentUser.displayName) return currentUser.displayName;
      const email = usage?.email || currentUser.email;
      if (email) {
        const emailName = email.split('@')[0];
        return emailName.charAt(0).toUpperCase() + emailName.slice(1);
      }
      return "You";
    };

    try {
      // Include session metadata from browser for AI context
      const sessionMetadata = getSessionMetadata();
      await chatApi.sendPyPrompt(
        prompt,
        chat,
        documents,
        setChat,
        employee,
        { ...contextSettings, ...sessionMetadata, requestAttachments: attachments },
        setPrompt,
        refreshChat,
        company,
        currentUser,
        id,
        true, // Enable structured response
        usage?.photo || "", // Pass user photo from usage
        attachments, // Pass attachments for message storage
        getUserDisplayName(), // Pass resolved user display name
        false, // includeAudio
        abortController.signal
      );
    } finally {
      // Re-enable sending after the request completes (success or error)
      setSendingChatId(null);
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }

    // Clear attachments after sending
    setImages([]);
    setRequestDocuments([]);
  };

  const handleDocumentChange = (e: any) => {
    var newContextSettings = {
      ...contextSettings,
      documentScope: e.target.value,
    };
    setContextSettings(newContextSettings);
    const selectedDoc = documents.find((x) => x.id === e.target.value);
    if (e.target.value === "root") {
      setFolderPath("/");
      getDocuments("/");
    }
    if (e.target.value === "/shared") {
      setFolderPath("/shared");
      getDocuments("/shared");
    }
    if (!selectedDoc) {
      return;
    }
    if (selectedDoc.type === "folder") {
      const newPath =
        selectedDoc.path !== "/"
          ? `${selectedDoc.path}/${selectedDoc.name}`
          : `/${selectedDoc.name}`;
      setFolderPath(newPath);
      getDocuments(newPath, selectedDoc);
      setContextSettings({ ...newContextSettings, documentPath: newPath });
    }
  };

  const handleFileChange = async (event: any) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const newImages: Array<{ name: string; content: string }> = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const base64 = await fileToBase64(file);
        newImages.push({ name: file.name, content: base64 });
      }
      setImages([...images, ...newImages]);
    }
  };

  const handleDocumentFileChange = async (event: any) => {
    const files = event.target.files;
    const formData = new FormData();
    for (var i = 0; i < files.length; i++) {
      formData.append("files", files[i]);
    }
    // Always generate summaries for large documents
    formData.append("generateSummary", "true");
    formData.append("companyId", companyId);
    setDocumentProcessing(true);
    const currentToken = await getToken();
    await axios
      .post(`${hostname}/documents/convert?companyId=${companyId}`, formData, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res: any) => {
        setRequestDocuments([...requestDocuments, ...res.data.documents]);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      })
      .finally(() => {
        setDocumentProcessing(false);
      });
  };

  // Process files from drag-and-drop or file input
  const processDroppedFiles = async (files: FileList) => {
    if (!files || files.length === 0) return;

    // Separate image files from document files
    const imageFiles = Array.from(files).filter((file: File) =>
      file.type.startsWith("image/")
    );
    const documentFiles = Array.from(files).filter((file: File) =>
      !file.type.startsWith("image/")
    );

    // Handle image files - convert all to base64
    if (imageFiles.length > 0 && availableModels.has(contextSettings.model)) {
      const newImages: Array<{ name: string; content: string }> = [];
      for (const imageFile of imageFiles) {
        const base64 = await fileToBase64(imageFile);
        newImages.push({ name: imageFile.name, content: base64 });
      }
      setImages([...images, ...newImages]);
    }

    // Handle document files
    if (documentFiles.length > 0) {
      const validDocFiles = documentFiles.filter(f => isAllowedDocumentType(f));
      const invalidFiles = documentFiles.filter(f => !isAllowedDocumentType(f));
      if (invalidFiles.length > 0) {
        const supportedTypes = ALLOWED_DOCUMENT_EXTENSIONS
          .map(ext => DOCUMENT_TYPE_LABELS[ext as keyof typeof DOCUMENT_TYPE_LABELS] || ext.toUpperCase())
          .join(", ");
        eventListener({
          type: "SET_ERROR",
          error: `Unsupported file type: ${invalidFiles.map(f => f.name).join(", ")}. Supported formats: ${supportedTypes}`,
        });
      }
      if (validDocFiles.length === 0) return;
      const formData = new FormData();
      for (const docFile of validDocFiles) {
        formData.append("files", docFile);
      }
      // Always generate summaries for large documents
      formData.append("generateSummary", "true");
      formData.append("companyId", companyId);
      setDocumentProcessing(true);
      const currentToken = await getToken();
      await axios
        .post(`${hostname}/documents/convert?companyId=${companyId}`, formData, {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        })
        .then((res: any) => {
          setRequestDocuments([...requestDocuments, ...res.data.documents]);
        })
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        })
        .finally(() => {
          setDocumentProcessing(false);
        });
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      await processDroppedFiles(files);
    }
  };

  // Handle paste events for file uploads (Cmd/Ctrl+V)
  const handlePaste = async (e: React.ClipboardEvent) => {
    const clipboardItems = e.clipboardData?.items;
    if (!clipboardItems) return;

    const files: File[] = [];
    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          files.push(file);
        }
      }
    }

    if (files.length > 0) {
      e.preventDefault(); // Prevent pasting file path as text
      const fileList = new DataTransfer();
      files.forEach((file) => fileList.items.add(file));
      await processDroppedFiles(fileList.files);
    }
  };

  // Copy last assistant response to clipboard
  const copyLastResponse = async () => {
    const assistantMessages = chat.filter((m: any) => m.role === "assistant");
    if (assistantMessages.length === 0) return;

    const lastMessage: any = assistantMessages[assistantMessages.length - 1];
    const textContent = lastMessage.content || lastMessage.text || "";

    try {
      await navigator.clipboard.writeText(textContent);
      eventListener({
        type: "SET_SUCCESS",
        message: "Response copied to clipboard",
      });
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: "Failed to copy to clipboard",
      });
    }
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + Shift + O: New chat
      if (modKey && e.shiftKey && e.key.toLowerCase() === "o") {
        e.preventDefault();
        setSelectEmployeesOpen(true);
      }

      // Cmd/Ctrl + Shift + C: Copy last response
      if (modKey && e.shiftKey && e.key.toLowerCase() === "c") {
        e.preventDefault();
        copyLastResponse();
      }

      // Cmd/Ctrl + /: Show keyboard shortcuts
      if (modKey && e.key === "/") {
        e.preventDefault();
        setShortcutsDialogOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [chat, employee]);

  if (loading) {
    return <LoadingScreen open={loading} />;
  }

  const settingsClose = () => {
    setSettingsOpen(false);
  };

  const saveSettings = () => {
    chatApi.saveChatSettings(id, contextSettings);
    setSettingsOpen(false);
  };

  const saveCompanyDefaultSettings = () => {
    chatApi.saveCompanyDefaultChatSettings(companyId, contextSettings);
    saveSettings();
  };

  // Handle project selection
  const handleProjectSelect = async (project: WorkspaceProject | null) => {
    if (project) {
      setCurrentProject(project);
      // Update context settings with projectId
      const newSettings = { ...contextSettings, projectId: project.id };
      setContextSettings(newSettings as any);
      // Save to chat settings
      chatApi.saveChatSettings(id, newSettings);
    } else {
      // Detach project
      setCurrentProject(null);
      setProjectFilesDialogOpen(false);
      const { projectId, ...settingsWithoutProject } = contextSettings as any;
      setContextSettings(settingsWithoutProject);
      chatApi.saveChatSettings(id, settingsWithoutProject);
    }
  };

  // Handle file selection from project files dialog
  const handleFileSelect = (file: ProjectFile) => {
    setSelectedFile(file);
    setFileViewerOpen(true);
  };

  const submitQuoteRequest = async (quoteData: {
    contactName: string;
    contactEmail: string;
    contactPhone?: string;
    message?: string;
  }) => {
    const token = await getToken();
    await axios.post(
      `${hostname}/send-quote-email`,
      {
        companyId,
        employeeId: employee.id,
        chatId: id,
        ...quoteData,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    eventListener({
      type: "SET_SUCCESS",
      message:
        "Quote email sent! The service provider will receive your conversation and contact details.",
    });
  };

  // Navbar height: 64px
  const navbarHeight = 64;

  // Employee header height (avatar + border)
  const employeeHeaderHeight = 91;

  // Input area base height
  const inputBaseHeight = 326;

  // Additional height when files/images are attached
  const attachmentHeight = requestDocuments.length > 0 || images.length > 0 ? 49 : 0;

  // Total input section height
  const inputSectionHeight = inputBaseHeight + attachmentHeight;

  // Total height to subtract from viewport
  const totalFixedHeight = navbarHeight + employeeHeaderHeight + inputSectionHeight;

  return (
    <Box>
      <ShareChatDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        companyId={companyId}
        chatId={id}
        getToken={getToken}
      />
      <ProjectFilesDialog
        open={projectFilesDialogOpen}
        onClose={() => setProjectFilesDialogOpen(false)}
        project={currentProject}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
        onFileSelect={handleFileSelect}
        onDetach={() => handleProjectSelect(null)}
        eventListener={eventListener as (event: { type: string; error?: string; message?: string }) => void}
      />
      <FileViewerDialog
        open={fileViewerOpen}
        onClose={() => {
          setFileViewerOpen(false);
          setSelectedFile(null);
        }}
        file={selectedFile}
        projectId={currentProject?.id || ""}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
      />
      <SelectEmployeeDialog
        open={selectEmployeesOpen}
        employees={employees}
        title={`Select ${employeeText}`}
        content={`Choose ${employeeLower} to chat with.`}
        buttonText="Submit"
        onClose={() => setSelectEmployeesOpen(false)}
        onSubmit={(employee: any) => {
          setSelectEmployeesOpen(false);
          createChat(employee);
        }}
      />
      <QuoteRequestDialog
        open={quoteDialogOpen}
        onClose={() => setQuoteDialogOpen(false)}
        employee={employee}
        chatId={id}
        companyId={companyId}
        onSubmitQuoteRequest={submitQuoteRequest}
      />
      <ChatContext
        message={dialogSelectedMessage}
        open={dialogSelectedMessage !== null}
        onClose={() => setDialogSelectedMessage(null)}
      />
      <Grid container spacing={0}>
        <Grid
          item
          display={{
            xs: id ? "none" : "block",
            md: "block",
            lg: "block",
          }}
          xs={id ? 2 : 12}
          md={3}
          lg={2.5}
          sx={{
            backgroundColor: instanceConfig.menuColor,
            borderRight:
              theme.palette.mode === "dark"
                ? "1px solid rgba(255, 255, 255, 0.12)"
                : "1px solid rgba(0, 0, 0, 0.12)",
          }}
        >
          <Button
            fullWidth
            sx={{ mt: 1, mb: 1 }}
            startIcon={<Add />}
            onClick={() => setSelectEmployeesOpen(true)}
          >
            New Chat
          </Button>
          <ChatLog
            ref={chatLogRef}
            style={{
              maxHeight: "calc(100vh - 125px)",
              height: "calc(100vh - 125px)",
              width: "100%",
              overflow: "scroll",
            }}
            companyId={companyId}
            getToken={getToken}
            currentUser={currentUser}
            currentChatId={id}
            perPage={25}
            eventListener={eventListener}
            onFirstChatLoad={handleFirstChatSelection}
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={9}
          lg={9.5}
          display={{ xs: displayValue, md: displayValue }}
        >
          {chatLoading && <LoadingScreen open={true} />}
          <Box
            sx={{
              height: `calc(100vh - ${navbarHeight}px)`,
              overflow: "hidden",
              display: chatLoading ? "none" : "flex",
              flexDirection: "row",
            }}
          >
            {/* Main Chat Area */}
            <Box
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              sx={{
                flex: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
            {/* Drag overlay */}
            {isDragging && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.1)"
                    : "rgba(0, 0, 0, 0.05)",
                  border: `3px dashed ${theme.palette.primary.main}`,
                  borderRadius: 2,
                  zIndex: 1000,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    padding: 3,
                    borderRadius: 2,
                    boxShadow: 3,
                    textAlign: "center",
                  }}
                >
                  <UploadFileOutlined sx={{ fontSize: 48, color: theme.palette.primary.main, mb: 1 }} />
                  <Typography variant="h6" color="primary">
                    Drop files here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {availableModels.has(contextSettings.model)
                      ? "Images, PDFs, Word docs, Excel files"
                      : "PDFs, Word docs, Excel files"}
                  </Typography>
                </Box>
              </Box>
            )}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                padding: 15,
                borderBottom:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.12)"
                    : "1px solid rgba(0, 0, 0, 0.12)",
                flexShrink: 0,
              }}
            >
              {/* Left side: Employee photo */}
              <Avatar
                src={employee.picture}
                style={{ height: 60, width: 60, marginRight: 10 }}
              />

              {/* Center: Employee name, title and CTA button */}
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography variant="h5" color="textSecondary">
                  {employee.name && employee.title
                    ? `${employee.name} (${employee.title})`
                    : ""}
                </Typography>
                {/* Show CTA button only for public employees from other companies with ctaText */}
                {employee.isPublicEmployee && employee.ctaText && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => setQuoteDialogOpen(true)}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      fontSize: "0.75rem",
                      height: 26,
                      borderRadius: 1,
                      width: "fit-content",
                      ml: "5px",
                    }}
                    startIcon={
                      <ContactMail sx={{ fontSize: "14px !important" }} />
                    }
                  >
                    {employee.ctaText}
                  </Button>
                )}
              </Box>

              {/* Right side: Action buttons */}
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Tooltip title="Chat sessions are private to each user — only you can see your conversations.">
                  <IconButton sx={{ cursor: "default" }} disableRipple>
                    <LucideIcon name="Lock" size={20} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Share chat"}>
                  <IconButton onClick={() => setShareOpen(true)}>
                    <IosShare />
                  </IconButton>
                </Tooltip>
                <Tooltip title={"Restart chat"}>
                  <IconButton onClick={() => restartChat(employee)}>
                    <RestartAltOutlined />
                  </IconButton>
                </Tooltip>
              </Box>
            </div>
            <Box
              ref={chatContainerRef}
              onScroll={handleScroll}
              sx={{
                flex: 1,
                overflow: "auto",
                pl: 2,
                pr: 2,
                overscrollBehavior: "contain",
                webkitOverflowScrolling: "touch",
              }}
            >
              <List>
                {!chat.length && (
                  <ListItem
                    style={{
                      padding: "0px, auto",
                    }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: "20px",
                        backgroundColor: "transparent",
                        border: "none",
                      }}
                    >
                      <CardHeader
                        style={{
                          float: "left",
                          width: "100%",
                        }}
                        avatar={
                          <Avatar
                            alt={employee.name}
                            sx={{ width: 30, height: 30 }}
                            src={employee.picture}
                          />
                        }
                        title={employee.name}
                      />
                      <CardContent>
                        {employee.greetingMessage ||
                          "Hello! How can I help you?"}
                      </CardContent>
                    </Card>
                  </ListItem>
                )}
                <ChatConversation
                  data-tutorial="chat-history"
                  chat={chat}
                  mode={mode}
                  eventListener={eventListener}
                  setDialogSelectedMessage={setDialogSelectedMessage}
                  autoScroll={autoScroll}
                  employees={employees}
                />
                <div ref={messagesEndRef} />
              </List>
            </Box>
            <div
              style={{
                padding: 10,
                borderTop:
                  theme.palette.mode === "dark"
                    ? "1px solid rgba(255, 255, 255, 0.12)"
                    : "1px solid rgba(0, 0, 0, 0.12)",
                flexShrink: 0,
              }}
            >
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
              >
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "stretch",
                        gap: 1,
                        padding: 1.5,
                        borderRadius: 3,
                        border: "1px solid",
                        borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.23)" : "rgba(0, 0, 0, 0.23)",
                        backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#ffffff",
                        "&:focus-within": {
                          borderColor: theme.palette.primary.main,
                          borderWidth: "2px",
                        },
                      }}
                    >
                      {/* Text input on top */}
                      <Box sx={{ position: "relative" }}>
                        <TextField
                          data-tutorial="chat-input"
                          multiline
                          minRows={3}
                          maxRows={9}
                          fullWidth
                          placeholder={
                            isVoiceChatActive
                              ? isVoiceRecording
                                ? voiceTranscript || "Listening..."
                                : isPlayingAudioRef.current
                                ? "Speaking..."
                                : "Listening..."
                              : "Type question here (Shift+Enter=New Line, Enter=Send Message)"
                          }
                          value={prompt}
                          onChange={(e) => setPrompt(e.target.value)}
                          variant="standard"
                          onPaste={handlePaste}
                          disabled={isVoiceChatActive}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              sendMessage();
                            }
                          }}
                          InputProps={{
                            disableUnderline: true,
                            sx: {
                              fontSize: "15px",
                              padding: 0,
                            },
                          }}
                          sx={{
                            "& .MuiInputBase-root": {
                              padding: 0,
                            },
                          }}
                        />

                        {/* Expand button - only show when text reaches 9 rows */}
                        {prompt.split('\n').length >= 9 && (
                          <IconButton
                            onClick={() => setPopOutDialogOpen(true)}
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              color: theme.palette.text.secondary,
                              opacity: 0.6,
                              zIndex: 2,
                              "&:hover": {
                                opacity: 1,
                              },
                            }}
                          >
                            <OpenInFullIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>

                      {/* Buttons row */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          flexShrink: 0,
                        }}
                      >
                        {/* Hidden file input */}
                        <input
                          accept={
                            availableModels.has(contextSettings.model)
                              ? `${DOCUMENT_ACCEPT_TYPES},image/*`
                              : DOCUMENT_ACCEPT_TYPES
                          }
                          style={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          multiple
                          onChange={(event: any) => {
                            const files = event.target.files;
                            if (files && files.length > 0) {
                              // Process all dropped/selected files (handles both images and documents)
                              processDroppedFiles(files);
                            }
                            setMenuAnchorEl(null);
                          }}
                          onClick={(event: any) => {
                            event.target.value = null;
                          }}
                        />

                        {/* Plus button on the left */}
                        <IconButton
                          onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                          sx={{ color: theme.palette.text.secondary, flexShrink: 0, mr: -1 }}
                          data-tutorial="chat-menu-btn"
                        >
                          <Add />
                        </IconButton>

                        {/* Keyboard shortcuts button - faint */}
                        <Tooltip title="Keyboard shortcuts">
                          <IconButton
                            onClick={() => setShortcutsDialogOpen(true)}
                            sx={{
                              color: theme.palette.text.secondary,
                              flexShrink: 0,
                              opacity: 0.4,
                              "&:hover": {
                                opacity: 0.8,
                              },
                            }}
                            size="small"
                          >
                            <KeyboardOptionKey fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {/* Menu for plus button */}
                        <Menu
                          anchorEl={menuAnchorEl}
                          open={menuOpen}
                          onClose={() => setMenuAnchorEl(null)}
                          anchorOrigin={{
                            vertical: "top",
                            horizontal: "left",
                          }}
                          transformOrigin={{
                            vertical: "bottom",
                            horizontal: "left",
                          }}
                        >
                          <MenuItem
                            onClick={() => {
                              document.getElementById("icon-button-file")?.click();
                            }}
                          >
                            <ListItemIcon>
                              <AttachFileOutlined fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Upload Files</ListItemText>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setMenuAnchorEl(null);
                              setScheduledTasksDialogOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <ScheduleIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Scheduled</ListItemText>
                          </MenuItem>
                          <MenuItem
                            onClick={() => {
                              setMenuAnchorEl(null);
                              setMemoriesDialogOpen(true);
                            }}
                          >
                            <ListItemIcon>
                              <EmojiObjectsIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText>Memories</ListItemText>
                          </MenuItem>
                          {/* Hide settings when locked for non-admin users */}
                          {!(employee?.isSettingsLocked && !hasAdminAccessByRole(userRole ?? null)) && (
                            <MenuItem
                              data-tutorial="chat-settings"
                              onClick={() => {
                                setMenuAnchorEl(null);
                                setSettingsOpen(true);
                              }}
                            >
                              <ListItemIcon>
                                <SettingsOutlined fontSize="small" />
                              </ListItemIcon>
                              <ListItemText>Chat Settings</ListItemText>
                            </MenuItem>
                          )}
                        </Menu>

                        {/* Scrollable container for uploaded files/images chips */}
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            overflowX: "auto",
                            flexGrow: 1,
                            alignItems: "center",
                            ml: 1,
                            "&::-webkit-scrollbar": {
                              display: "none",
                            },
                            scrollbarWidth: "none", // Firefox
                            msOverflowStyle: "none", // IE and Edge
                          }}
                        >
                          {/* Project chip */}
                          {currentProject && (
                            <Chip
                              icon={<FolderIcon fontSize="small" />}
                              label={currentProject.name}
                              variant="outlined"
                              size="small"
                              color="default"
                              onClick={() => setProjectFilesDialogOpen(true)}
                              onDelete={() => handleProjectSelect(null)}
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                          {images.map((img, idx) => (
                            <Tooltip
                              key={idx}
                              title={
                                <Box sx={{ p: 0.5 }}>
                                  <Box
                                    component="img"
                                    src={img.content}
                                    alt={img.name}
                                    sx={{
                                      maxWidth: 300,
                                      maxHeight: 200,
                                      objectFit: "contain",
                                    }}
                                  />
                                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                                    {img.name}
                                  </Typography>
                                </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <Chip
                                avatar={<Avatar src={addImageDimensions(img.content, 48, 48)} sx={{ width: 24, height: 24 }} />}
                                label={img.name || "Image"}
                                variant="outlined"
                                size="small"
                                onDelete={() => {
                                  setImages(images.filter((_, i) => i !== idx));
                                }}
                                sx={{ flexShrink: 0 }}
                              />
                            </Tooltip>
                          ))}
                          {documentProcessing && (
                            <Chip
                              icon={<CircularProgress size={16} />}
                              label="Processing document..."
                              variant="outlined"
                              size="small"
                              sx={{ flexShrink: 0 }}
                            />
                          )}
                          {requestDocuments.map((document) => (
                            <Tooltip
                              key={document.id}
                              title={
                                <Box
                                  sx={{
                                    maxWidth: 400,
                                    maxHeight: 300,
                                    overflow: "auto",
                                    p: 1,
                                  }}
                                >
                                  <Typography variant="caption" component="pre" sx={{ whiteSpace: "pre-wrap", fontFamily: "inherit" }}>
                                    {document.content && document.content.length > 1000
                                      ? document.content.substring(0, 1000) + "..."
                                      : document.content || "No content available"}
                                  </Typography>
                                </Box>
                              }
                              placement="top"
                              arrow
                            >
                              <Chip
                                icon={<DescriptionOutlined />}
                                label={document.name}
                                variant="outlined"
                                size="small"
                                onDelete={() =>
                                  setRequestDocuments(
                                    requestDocuments.filter(
                                      (x) => x.id !== document.id
                                    )
                                  )
                                }
                                sx={{ flexShrink: 0 }}
                              />
                            </Tooltip>
                          ))}
                        </Box>

                        {/* Dictation button - hide during voice chat */}
                        {!isVoiceChatActive && (
                          <Tooltip title={isDictating ? "Stop dictation" : "Start dictation (Beta)"}>
                            <IconButton
                              onClick={isDictating ? stopDictation : startDictation}
                              disabled={isDictationLoading}
                              sx={{
                                color: isDictating
                                  ? theme.palette.error.main
                                  : theme.palette.text.secondary,
                                width: 40,
                                height: 40,
                                animation: isDictating ? "pulse 1.5s infinite" : "none",
                                "@keyframes pulse": {
                                  "0%": { opacity: 1 },
                                  "50%": { opacity: 0.5 },
                                  "100%": { opacity: 1 },
                                },
                              }}
                            >
                              {isDictationLoading ? (
                                <CircularProgress size={20} />
                              ) : isDictating ? (
                                <Stop />
                              ) : (
                                <Mic />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}

                        {/* Send / Stop / Voice chat button.
                            While sending, the Send arrow is swapped for a Stop square in
                            the same slot — voice chat slot is unaffected. */}
                        {sendingChatId === id ? (
                          <Tooltip title="Stop">
                            <IconButton
                              onClick={() => {
                                if (abortControllerRef.current) {
                                  abortControllerRef.current.abort();
                                  abortControllerRef.current = null;
                                }
                              }}
                              sx={{
                                backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                                color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
                                width: 40,
                                height: 40,
                                "&:hover": {
                                  backgroundColor: theme.palette.mode === "dark" ? "#f0f0f0" : "#333333",
                                },
                              }}
                            >
                              <Stop sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        ) : prompt.trim() ? (
                          <IconButton
                            type="submit"
                            disabled={!prompt.trim() || isDictating || isVoiceChatActive}
                            sx={{
                              backgroundColor: theme.palette.mode === "dark" ? "#ffffff" : "#000000",
                              color: theme.palette.mode === "dark" ? "#000000" : "#ffffff",
                              width: 40,
                              height: 40,
                              "&:hover": {
                                backgroundColor: theme.palette.mode === "dark" ? "#f0f0f0" : "#333333",
                              },
                              "&:disabled": {
                                backgroundColor: theme.palette.action.disabledBackground,
                                color: theme.palette.action.disabled,
                              },
                            }}
                          >
                            <ArrowUpward />
                          </IconButton>
                        ) : (
                          <>
                            {isVoiceChatActive && (
                              <Chip
                                label="Beta"
                                size="small"
                                sx={{
                                  height: 20,
                                  fontSize: "0.7rem",
                                  backgroundColor: theme.palette.action.hover,
                                  color: theme.palette.text.secondary,
                                }}
                              />
                            )}
                            <Tooltip title={isVoiceChatActive ? "End voice chat" : "Start voice chat"}>
                              <span>
                                <IconButton
                                  onClick={toggleVoiceChat}
                                  disabled={isDictating}
                                  sx={{
                                    backgroundColor: isVoiceChatActive
                                      ? theme.palette.action.disabledBackground
                                      : theme.palette.mode === "dark"
                                      ? "#ffffff"
                                      : "#000000",
                                    color: isVoiceChatActive
                                      ? theme.palette.text.secondary
                                      : theme.palette.mode === "dark"
                                      ? "#000000"
                                      : "#ffffff",
                                    width: 40,
                                    height: 40,
                                    animation: isVoiceRecording ? "voicePulse 1.5s infinite" : "none",
                                    "@keyframes voicePulse": {
                                      "0%": { opacity: 1 },
                                      "50%": { opacity: 0.6 },
                                      "100%": { opacity: 1 },
                                    },
                                    "&:hover": {
                                      backgroundColor: isVoiceChatActive
                                        ? theme.palette.action.hover
                                        : theme.palette.mode === "dark"
                                        ? "#f0f0f0"
                                        : "#333333",
                                    },
                                    "&:disabled": {
                                      backgroundColor: theme.palette.action.disabledBackground,
                                      color: theme.palette.action.disabled,
                                    },
                                  }}
                                >
                                  <GraphicEq />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                  <Grid item xs={12} style={{ textAlign: "center" }}>
                    <Typography variant="caption">
                      {whitelabelSettings?.title || "GTM AI"} can make
                      mistakes. Consider checking important information.
                    </Typography>
                  </Grid>
                  <Grid></Grid>
                </Grid>
              </form>
            </div>
          </Box>
          </Box>
        </Grid>
      </Grid>
      <ChatSettingsDialog
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        contextSettings={contextSettings}
        setContextSettings={setContextSettings}
        saveSettings={saveSettings}
        saveCompanyDefaultSettings={saveCompanyDefaultSettings}
        strategyOptions={strategyOptions}
        company={company}
        currentPlan={currentPlan}
        getToken={getToken}
        eventListener={eventListener}
        currentProject={currentProject}
        onProjectSelect={handleProjectSelect}
      />
      <PopOutTextDialog
        open={popOutDialogOpen}
        onClose={() => setPopOutDialogOpen(false)}
        onSave={(newValue) => {
          setPrompt(newValue);
          setPopOutDialogOpen(false);
        }}
        value={prompt}
        title="Compose Message"
        description="Type your message here"
        placeholder="Message..."
        rows={20}
      />
      {/* Keyboard Shortcuts Dialog */}
      <Dialog
        open={shortcutsDialogOpen}
        onClose={() => setShortcutsDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <KeyboardOptionKey />
          Keyboard Shortcuts
          <IconButton
            onClick={() => setShortcutsDialogOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
            size="small"
          >
            <Typography variant="body2" color="textSecondary">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <List dense>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>Enter</strong></Typography>}
                secondary="Send message"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>Shift + Enter</strong></Typography>}
                secondary="New line"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>{navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌘" : "Ctrl"} + V</strong></Typography>}
                secondary="Paste files/images from clipboard"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>{navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌘" : "Ctrl"} + Shift + O</strong></Typography>}
                secondary="New chat"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>{navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌘" : "Ctrl"} + Shift + C</strong></Typography>}
                secondary="Copy last response"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={<Typography variant="body2"><strong>{navigator.platform.toUpperCase().indexOf("MAC") >= 0 ? "⌘" : "Ctrl"} + /</strong></Typography>}
                secondary="Show this help"
              />
            </ListItem>
          </List>
        </DialogContent>
      </Dialog>
      {/* Saved Prompts Dialog */}
      <Dialog
        open={promptsDialogOpen}
        onClose={() => setPromptsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StickyNote2Outlined />
            Saved Prompts
          </Box>
          <IconButton
            onClick={() => setPromptsDialogOpen(false)}
            size="small"
          >
            <Typography variant="body2" color="textSecondary">×</Typography>
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {promptsLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : savedPrompts.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4 }}>
              <Typography variant="body1" color="textSecondary" gutterBottom>
                No saved prompts yet
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Create prompts in Prompt Studio to use them here
              </Typography>
            </Box>
          ) : (
            <List>
              {savedPrompts.map((savedPrompt) => (
                <ListItem
                  key={savedPrompt.id}
                  onClick={() => handleSelectPrompt(savedPrompt.content)}
                  sx={{
                    cursor: "pointer",
                    borderRadius: 1,
                    mb: 1,
                    border: "1px solid",
                    borderColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
                    "&:hover": {
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ListItemIcon>
                    <StickyNote2Outlined fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary={savedPrompt.name}
                    secondary={savedPrompt.description || savedPrompt.content.substring(0, 100) + (savedPrompt.content.length > 100 ? "..." : "")}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>
      {composioInstallSlug && (
        <InstallIntegrationDialog
          open={composioInstallOpen}
          onClose={() => {
            setComposioInstallOpen(false);
            setComposioInstallSlug(null);
          }}
          selectedIntegration={composioInstallSlug}
          getToken={getToken}
          eventListener={eventListener}
          company={company}
          companyId={companyId}
          onSaveSuccess={async () => {
            eventListener({
              type: "SET_SUCCESS",
              message: "Integration installed successfully. You can now retry your request.",
            });
            setComposioInstallOpen(false);
            setComposioInstallSlug(null);
          }}
        />
      )}
      <MemoriesDialog
        open={memoriesDialogOpen}
        onClose={() => setMemoriesDialogOpen(false)}
        companyId={companyId}
        getToken={getToken as () => Promise<string>}
      />
      <ScheduledTasksDialog
        open={scheduledTasksDialogOpen}
        onClose={() => setScheduledTasksDialogOpen(false)}
        companyId={companyId}
        companyName={company?.name || ""}
        getToken={getToken as () => Promise<string>}
        employees={employees}
        currentPlan={currentPlan}
      />
    </Box>
  );
};
